/**
 * A rolled-up quantity, turned into words - apart from anything that draws it.
 *
 * Every function here is pure and exported for one reason: a total renders as a
 * plausible number whatever the code did to it. "141.85 kg" looks exactly as
 * convincing when it should have said "≥ 141.85 kg", when three parts below it
 * state nothing at all, and when the honest answer was that nobody has filled
 * anything in. A render test that finds a number on screen cannot tell those
 * apart, so the decisions live here and the tests drive them directly.
 *
 * `PartTotal` carries four states and collapsing them into one figure is the
 * failure the shape exists to prevent:
 *
 *   - a FIGURE - `141.85 kg`, and a stated `0 W` is one of these. Zero is an
 *     answer;
 *   - a BOUND - `bound`, because something below was counted short: a
 *     multiplicity with no upper bound, or a part that states nothing. Rendered
 *     `≥ 141.85 kg`, never the bare number: the bare number is a claim the model
 *     does not make. It points DOWN for a sum and UP for a `min`, where an
 *     uncounted part can only make the weakest link look stronger - so a
 *     ceiling drawn as a floor reads a shortfall as a margin;
 *   - PARTIAL - something below states nothing, and `unstated` NAMES it. Said as
 *     "partial — FSW states no dry mass", because "partial" on its own tells
 *     nobody what to go and fill in. Unset is not zero;
 *   - UNKNOWABLE - `total` is null, because nothing below stated anything. Shown
 *     as "not stated" and emphatically not as 0.
 *
 * `problems` is separate from all four and must be surfaced separately: the
 * server refuses to guess at a value in the wrong unit or a value nobody can
 * parse, and swallowing the refusal turns it into a silent wrong answer. Note
 * from the fixtures that a total can carry problems and NOT be partial, so the
 * two cannot be folded together.
 *
 * WHICH quantities appear is read from the payload and never written here. An
 * attribute rolls up because it carries `@EchelonSysML::RollsUp` in the model, so
 * a program that declares timing, memory or delta-V of its own must appear with
 * no edit on this side - which is the entire reason the marker lives in the
 * model. A hard-coded "Dry mass" column would defeat it.
 */

import type { KindSchema, PartTotal, RollingAttribute } from '../../schema'

/** What a total is shown as when nothing below it stated anything. Not '0'. */
export const UNSTATED_TOTAL = 'not stated'

/** What is shown when the payload carried no total for a column at all. */
export const NO_TOTAL = 'no total'

/** The floor marker. Its absence would be a claim the model does not make. */
export const MINIMUM_MARK = '≥'

/** The ceiling marker, for a 'min' whose subtree is incomplete. */
export const MAXIMUM_MARK = '≤'

/** What a usage shows in an `own` cell: it never states a value of its own. */
export const NOT_OWN = '—'

export const ROLLUP_NOTE =
  'Each row also carries the total over everything below it. ' +
  '“≥” means something below was counted short, so the figure is a floor rather ' +
  'than the answer, and “≤” means the same for a quantity that aggregates as its ' +
  'weakest part, where the figure can only come down; “partial” names the parts ' +
  'that state nothing, which is not the same as their stating zero.'

export const NO_ROLLUP_NOTE =
  'Nothing in this model is marked as rolling up, so there is no total to show. ' +
  'Which quantities aggregate is stated in the model by @EchelonSysML::RollsUp, ' +
  'not by this screen.'

/** One column of totals: what to call it, and the unit its figures are in. */
export interface RollupColumn {
  /** The key into `totals`, and the registry field name. */
  name: string
  label: string
  unit: string
}

/**
 * The last segment of a unit reference, which is what a person reads.
 *
 * `SI::kg` is written as `kg` for the same reason the server compares units on
 * their last segment: a model may write either, and they are the same statement.
 *
 * @param unit the unit reference as the payload carries it
 */
export function shortUnit(unit: string): string {
  return (unit || '').split('::').pop() ?? ''
}

/**
 * A number as text, with the float noise taken off and nothing else changed.
 *
 * Three decimals, because a 0.05 kg fastener is a real row in the corpus and
 * `141.85000000000002` is what summing floats actually produces. No thousands
 * separator and no locale: a fixture that passes in one locale and fails in
 * another is worse than a plain number.
 *
 * @param value the figure
 */
export function formatNumber(value: number): string {
  const rounded = Number(value.toFixed(3))
  // -0 prints as '-0', which reads as a negative mass.
  return Object.is(rounded, -0) ? '0' : String(rounded)
}

/**
 * A figure with its unit - `141.85 kg`.
 *
 * @param value the figure
 * @param unit the unit reference, e.g. `SI::kg`
 */
export function formatQuantity(value: number, unit: string): string {
  const written = shortUnit(unit)
  return written ? `${formatNumber(value)} ${written}` : formatNumber(value)
}

/**
 * Name the parts responsible, and stop before the list becomes wallpaper.
 *
 * Three names, then a count. Naming them is the point - "partial" alone tells
 * nobody what to go and fill in - but a row that names forty parts is read by
 * nobody, and the pane's tooltip carries the whole list anyway.
 *
 * @param names the part names, as `unstated` carries them
 */
export function listNames(names: readonly string[]): string {
  const shown = names.slice(0, 3)
  const rest = names.length - shown.length
  if (rest > 0) return `${shown.join(', ')} and ${rest} more`
  if (shown.length <= 1) return shown.join('')
  return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`
}

/** One total, ready to render: what it says, and everything it qualifies. */
export interface TotalReading {
  /** False when there is no figure - `text` then says so rather than showing 0. */
  known: boolean
  /** `≥ 141.85 kg`, `0 W`, `not stated`. Never a bare figure when it is a floor. */
  text: string
  isFloor: boolean
  /** The opposite caveat: a weakest-link figure that can only come DOWN. */
  isCeiling: boolean
  isPartial: boolean
  /** 'partial — FSW states no dry mass', or ''. Names the parts. */
  qualifier: string
  /** The same fact, short enough for a tree row: 'partial: FSW'. */
  partialShort: string
  /** Why the figure is bounded rather than exact, or ''. */
  boundNote: string
  /** What could not be summed, in the server's own words. */
  problems: string[]
  /** '2 values could not be summed', or ''. */
  problemsShort: string
  /** Everything above in one sentence, for a tooltip or an aria-label. */
  detail: string
}

/** 'Dry mass' mid-sentence. Only the first letter, so 'Delta-V' survives. */
function midSentence(label: string): string {
  return label ? label.charAt(0).toLowerCase() + label.slice(1) : 'value'
}

/**
 * How a total reads, in every one of its states.
 *
 * @param total the total for this part and column, or nothing if the payload
 *              carried none - which is a different answer from a null total
 * @param column the column it belongs to, for the label and the fallback unit
 */
export function readTotal(
  total: PartTotal | null | undefined,
  column: RollupColumn,
): TotalReading {
  const quantity = midSentence(column.label)

  if (!total) {
    return {
      known: false,
      text: NO_TOTAL,
      isFloor: false,
      isCeiling: false,
      isPartial: false,
      qualifier: '',
      partialShort: '',
      boundNote: '',
      problems: [],
      problemsShort: '',
      detail: `The payload carried no ${quantity} total for this part, so there is nothing to show.`,
    }
  }

  const unit = total.unit || column.unit
  const known = total.total !== null
  // The floor marker goes INSIDE the text rather than beside it, so no caller can
  // render the figure and leave the marker behind.
  let text = UNSTATED_TOTAL
  if (total.total !== null) {
    const figure = formatQuantity(total.total, unit)
    if (total.bound === 'at_least') text = `${MINIMUM_MARK} ${figure}`
    else if (total.bound === 'at_most') text = `${MAXIMUM_MARK} ${figure}`
    else text = figure
  }

  const problems = [...total.problems]
  const problemsShort = problems.length
    ? `${problems.length} value${problems.length === 1 ? '' : 's'} could not be summed`
    : ''

  // Two directions, and which one applies is the payload's answer. A weakest
  // link computed without one of the links looks STRONGER than the assembly is,
  // so the same incompleteness that makes a sum a floor makes a minimum a
  // ceiling - and the note has to say which, or a reader takes a shortfall for
  // a margin.
  let boundNote = ''
  if (total.bound === 'at_least') {
    boundNote =
      `Something below was counted short - a multiplicity with no fixed upper bound, ` +
      `or a part that states nothing - so the real ${quantity} can only be higher.`
  } else if (total.bound === 'at_most') {
    boundNote =
      `This aggregates as a minimum - the weakest part - and something below states ` +
      `no ${quantity}, so the real figure can only be lower.`
  }

  let qualifier = ''
  let partialShort = ''
  if (total.is_partial) {
    if (total.unstated.length > 0) {
      const verb = total.unstated.length === 1 ? 'states' : 'state'
      qualifier = `partial — ${listNames(total.unstated)} ${verb} no ${quantity}`
      partialShort = `partial: ${listNames(total.unstated)}`
    } else if (problems.length > 0) {
      // Partial with nothing named: the gap is a REFUSED value rather than a
      // missing one, and saying "something states nothing" would name the wrong
      // fault. The fixtures have this row.
      qualifier = 'partial — something below could not be counted, and is named below'
      partialShort = 'partial: see the problems'
    } else {
      qualifier = `partial — something below states no ${quantity}`
      partialShort = 'partial'
    }
  }

  const absence = known
    ? ''
    : `Nothing below states a ${quantity}, so there is no figure - which is not the same as zero.`

  return {
    known,
    text,
    isFloor: total.bound === 'at_least',
    isCeiling: total.bound === 'at_most',
    isPartial: total.is_partial,
    qualifier,
    partialShort,
    boundNote,
    problems,
    problemsShort,
    detail: [`${column.label}: ${text}.`, absence, qualifier, boundNote, ...problems]
      .filter(Boolean)
      .join(' '),
  }
}

/** A part's own stated value, and why it is absent when it is. */
export interface OwnReading {
  /** '40 kg', 'not stated', '—', or the stated text that was refused. */
  text: string
  /** True only when this figure went into the total. */
  counted: boolean
  /** Why it reads as it does. Never empty when `counted` is false. */
  note: string
}

/**
 * A part's own value, beside the total of what it contains.
 *
 * The distinction is the point of the feature - what a part weighs itself is not
 * what its subtree weighs - and it has three absences that a single "not stated"
 * would flatten into one:
 *
 *   - a USAGE states nothing of its own, ever. Its value is on the definition it
 *     is typed by, and a value typed onto a usage is REFUSED by the server
 *     rather than counted, which the note has to say;
 *   - a definition that states nothing is a gap somebody can go and fill;
 *   - a definition whose stated value could not be used - the wrong unit, or not
 *     a number at all - has a value and it is not counted. Rendering that as
 *     "not stated" would send somebody looking for a blank field that is full.
 *
 * @param total this part's total for the column, which carries `own`
 * @param column the column, for its label and unit
 * @param isDefinition whether this part is a definition
 * @param stated the value the part stores as TEXT, when the caller has it
 */
export function readOwn(
  total: PartTotal | null | undefined,
  column: RollupColumn,
  isDefinition: boolean,
  stated?: unknown,
): OwnReading {
  const quantity = midSentence(column.label)
  const unit = total?.unit || column.unit
  const written = String(stated ?? '').trim()

  if (total && total.own !== null) {
    return { text: formatQuantity(total.own, unit), counted: true, note: '' }
  }

  if (written) {
    return {
      text: `${written} — not counted`,
      counted: false,
      note: isDefinition
        ? `This part states a ${quantity}, but it could not be used - the problems below ` +
          `say why. It was left out of the total rather than guessed at.`
        : `A value stated on a usage never overrides its definition, so this one was not ` +
          `counted. Two parts of differing ${quantity} is what specializing the ` +
          `definition is for.`,
    }
  }

  if (!isDefinition) {
    return {
      text: NOT_OWN,
      counted: false,
      note:
        `A usage states no ${quantity} of its own - it is on the definition this is ` +
        `typed by, so every usage of that definition reports the same figure.`,
    }
  }

  return {
    text: UNSTATED_TOTAL,
    counted: false,
    note:
      `This definition states no ${quantity}. It contributes nothing and marks every ` +
      `total above it partial - unset is not zero.`,
  }
}

/** 'dry_mass' with no registry entry behind it still reads as 'Dry mass'. */
function humanize(name: string): string {
  const words = name.replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : name
}

/**
 * Which columns of totals to show, from the payload rather than from a list.
 *
 * `declared` is the authority when the caller has it: `/api/parts/{id}` answers
 * `attributes`, which is the model's own statement of what aggregates, in
 * declaration order. `GET /api/design` answers the same list beside its rows, but
 * `api.design()` keeps only the rows - so the tree infers the columns from the
 * KEYS of the totals it was given, which the server fills for exactly the
 * attributes it rolled up. Either way nothing here names a quantity, which is
 * what lets a program-declared one appear untouched.
 *
 * The registry supplies the LABEL when the columns are inferred, because a field
 * added to the part kind carries one - and `humanize` covers a rolling attribute
 * BEST has no field for, which is a real case rather than a defensive one.
 *
 * Order is the payload's own, so the screen agrees with the model's declaration
 * order rather than imposing one.
 *
 * @param declared the attributes the payload says roll up, when it says
 * @param rows anything carrying a `totals` map - design rows, or one detail
 * @param kind the part kind from /api/schema, for labels when inferring
 */
export function rollupColumns(
  declared: RollingAttribute[] | null | undefined,
  rows: readonly { totals?: Record<string, PartTotal> | null }[],
  kind?: KindSchema | null,
): RollupColumn[] {
  if (declared && declared.length > 0) {
    return declared.map((entry) => ({
      name: entry.name,
      label: entry.label || labelFor(kind, entry.name),
      unit: entry.unit,
    }))
  }

  const columns: RollupColumn[] = []
  const seen = new Set<string>()
  for (const row of rows) {
    for (const [name, total] of Object.entries(row.totals ?? {})) {
      if (seen.has(name)) continue
      seen.add(name)
      columns.push({ name, label: labelFor(kind, name), unit: total?.unit ?? '' })
    }
  }
  return columns
}

/** The registry's label for a field, or a readable version of its name. */
function labelFor(kind: KindSchema | null | undefined, name: string): string {
  const field = kind?.fields.find((entry) => entry.name === name)
  return field?.label || humanize(name)
}

/**
 * What to say about a quantity the model rolls up and BEST cannot store.
 *
 * Said rather than dropped. A value is stored whatever the attribute is; what
 * `unstorable` means now is that nothing says what UNIT it is in, and a total
 * needs one - two values in different units must not be added. Silently
 * ignoring it leaves somebody holding a model that says a quantity rolls up and
 * a screen that never mentions it.
 *
 * @param names the attribute names, as `unstorable` carries them
 * @returns the sentence, or '' when there are none
 */
export function unstorableNote(names: readonly string[]): string {
  if (names.length === 0) return ''
  const one = names.length === 1
  return (
    `${listNames(names)} ${one ? 'is' : 'are'} marked as rolling up in the model, but ` +
    `nothing says what unit ${one ? 'it is' : 'they are'} in, so there is no total for ` +
    `${one ? 'it' : 'them'} yet - only a name.`
  )
}

/**
 * Everything that could not be summed, once each, in column order.
 *
 * Deduplicated because the same refused value reaches several columns' totals
 * through the same subtree, and repeated verbatim otherwise: the server's message
 * already names the part and the quantity, so a prefix here would say it twice.
 *
 * @param totals the part's totals, keyed by column
 * @param columns the columns being shown, which fixes the order
 */
export function rollupProblems(
  totals: Record<string, PartTotal> | null | undefined,
  columns: readonly RollupColumn[],
): string[] {
  const found: string[] = []
  const seen = new Set<string>()
  for (const column of columns) {
    for (const problem of totals?.[column.name]?.problems ?? []) {
      if (seen.has(problem)) continue
      seen.add(problem)
      found.push(problem)
    }
  }
  return found
}
